import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocompletion using the French government BAN (Base Adresse Nationale).
 * Free, no API key required.
 *
 * https://adresse.data.gouv.fr/api-doc/adresse
 */
const BAN_ENDPOINT = "https://api-adresse.data.gouv.fr/search/";

export default function AddressAutocomplete({ value, onSelect, onChange, placeholder = "Rue, ville…" }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = (q) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    fetch(`${BAN_ENDPOINT}?q=${encodeURIComponent(q)}&limit=6&autocomplete=1`, {
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        setSuggestions(data.features || []);
        setOpen(true);
        setActiveIdx(-1);
      })
      .catch((e) => {
        if (e.name !== "AbortError") console.error(e);
      })
      .finally(() => setLoading(false));
  };

  const handleQueryChange = (next) => {
    setQuery(next);
    onChange?.(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!next || next.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(next), 220);
  };

  const handlePick = (feature) => {
    const props = feature.properties || {};
    setQuery(props.label || "");
    setOpen(false);
    setSuggestions([]);
    onSelect?.({
      label: props.label,
      housenumber: props.housenumber,
      street: props.street || props.name,
      city: props.city,
      citycode: props.citycode,
      postcode: props.postcode,
      context: props.context,
      coordinates: feature.geometry?.coordinates, // [lon, lat]
    });
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handlePick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid="address-autocomplete">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
        <Input
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="h-11 pl-9 pr-9 mt-1.5 bg-white"
          data-testid="address-input"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-auto divide-y divide-slate-100"
          data-testid="address-suggestions"
        >
          {suggestions.map((f, i) => {
            const p = f.properties || {};
            return (
              <li
                key={`${p.id || i}`}
                onClick={() => handlePick(f)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`px-3.5 py-2.5 text-sm cursor-pointer flex items-start gap-3 ${
                  activeIdx === i ? "bg-blue-50" : "hover:bg-slate-50"
                }`}
                data-testid={`address-option-${i}`}
              >
                <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.label}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {p.postcode} {p.city} {p.context ? `· ${p.context}` : ""}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
